import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Layout,
  Input,
  Button,
  Form,
  Card,
  Divider,
  Table,
  MenuProps,
  Dropdown,
  Popover,
  Tag,
  Image,
} from "antd";
import Loader from "../../components/Loader/Loader";
import "./OverviewMaterialsPage.css";
import Sidebar from "../../components/Sidebar/Sidebar";
import Header from "../../components/Header/Header";
import Copyright from "../../components/Copyright/Copyright";
import {
  getBuyersService,
  getCategoriesService,
  getMetalsService,
  getOverviewMaterialsService,
  getSuppliersService,
} from "./OverviewMaterialsPageService";
import { removeTokenFromCookies } from "../../utils/cookies";
import Cookies from "universal-cookie";
import { ControlFilled, EditOutlined, MoreOutlined } from "@ant-design/icons";

const { Content } = Layout;

export interface OverviewMaterialsPageInterface {}

const OverviewMaterialsPage = (props: OverviewMaterialsPageInterface) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [loading, setLoading] = useState(false);
  const [metals, setMetals] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalOverviewMaterials, setTotalOverviewMaterials] = useState(0);
  const [overviewMaterialsList, setOverviewMaterialsList] = useState([]) as any;
  const [metalsMap, setMetalsMap] = useState({}) as any;
  const [categories, setCategories] = useState([]) as any;
  const [suppliers, setSuppliers] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const navigate = useNavigate();
  const cookies = new Cookies();
  const currOffice = cookies.get("CURR_OFFICE");

  const materialsColumns = [
    {
      title: "Item Number",
      key: "index",
      align: "center" as const,
      render: (text: any, record: any, index: any) => index + 1,
    },
    {
      title: "Purchase Date",
      key: "purchaseDate",
      align: "center" as const,
      render: (record: any) => new Date(record.purchaseCreatedTimestamp).toLocaleDateString("en-GB"),
    },
    {
      title: "Office",
      key: "office",
      align: "center" as const,
      render: (record: any) => {
        let officeNameFormat: any = {
          pg: "Penang (PG)",
          bw: "Butterworth (BW)",
        };
        return (
          <div style={{ display: "flex" }}>
            <div className={`${record.office}-square-icon`}></div>
            <div> {officeNameFormat[record.office]}</div>
          </div>
        );
      },
    },
    {
      title: "Supplier",
      key: "supplier",
      align: "left" as const,
      render: (record: any) => getSupplierDisplayName(record.supplierID),
    },
    {
      title: "Image",
      key: "index",
      render: (record: any) => (
        <>
          <Image
            height={50}
            width={50}
            src={record.scaleImageURL || record.imageURL}
            preview={{
              toolbarRender: () => (
                <>
                  <h1 style={{ color: "#000000" }}>{metalsMap[record.metalID].displayName}</h1>
                  <h3 style={{ color: "#000000" }}>Raw Weight: {Number(record.rawWeight).toFixed(1)} kg</h3>
                </>
              ),
            }}
          />
          <Image
            height={50}
            width={50}
            src={record.materialImageURL}
            preview={{
              toolbarRender: () => (
                <>
                  <h1 style={{ color: "#000000" }}>{metalsMap[record.metalID].displayName}</h1>
                  <h3 style={{ color: "#000000" }}>Raw Weight: {Number(record.rawWeight).toFixed(1)} kg</h3>
                </>
              ),
            }}
          />
        </>
      ),
      align: "center" as const,
    },
    {
      title: "Material Category",
      key: "name",
      align: "left" as const,
      render: (record: any) => {
        const categoryID = metalsMap[record.metalID]?.categoryID;
        const category = categories.find((category: any) => category.categoryID === categoryID);
        return (
          <div style={{ display: "flex" }}>
            <div className={`${category.name.toLowerCase().replace(" ", "-")}-square-icon`}></div>
            <div> {category !== undefined ? category.name.replace("Stainless", "") : ""}</div>
          </div>
        );
      },
    },
    {
      title: "Material Name",
      key: "name",
      align: "left" as const,
      render: (record: any) =>
        metalsMap[record.metalID] !== undefined ? metalsMap[record.metalID].displayName : record.metalID,
    },
    {
      title: "Margin (RM)",
      key: "margin",
      align: "center" as const,
      render: (record: any) => {
        let profitMarginColor = "grey";
        if (Number(record.margin) < 0.0) profitMarginColor = "red";
        if (Number(record.margin) > 0.0 && Number(record.margin) <= 0.3) profitMarginColor = "volcano";
        if (Number(record.margin) > 0.3 && Number(record.margin) <= 0.5) profitMarginColor = "orange";
        if (Number(record.margin) > 0.5 && Number(record.margin) <= 0.8) profitMarginColor = "gold";
        if (Number(record.margin) > 0.8 && Number(record.margin) <= 1.0) profitMarginColor = "lime";
        if (Number(record.margin) > 1.0) profitMarginColor = "green";
        return record.margin == 0 ? "-" : <Tag color={profitMarginColor}>{Number(record.margin).toFixed(2)}</Tag>;
      },
    },
    {
      title: "Net Weight (kg)",
      key: "netWeight",
      align: "center" as const,
      render: (record: any) => Number(record.netWeight).toFixed(1),
    },
    {
      title: "Purchase Price (RM)",
      key: "purchasePrice",
      align: "center" as const,
      render: (record: any) => Number(record.purchasePrice).toFixed(2),
    },
    {
      title: "Purchase Cost (RM)",
      key: "purchaseCost",
      align: "center" as const,
      render: (record: any) => Number(record.purchaseCost).toFixed(2),
    },

    {
      title: "Sold Date",
      key: "soldDate",
      align: "center" as const,
      render: (record: any) =>
        record.soldTimestamp !== null ? new Date(record.soldTimestamp).toLocaleDateString("en-GB") : "-",
    },
    {
      title: "Buyer",
      key: "buyer",
      align: "center" as const,
      render: (record: any) => (record.buyerID ? getBuyerDisplayName(record.buyerID) : "-"),
      //check buyerID
    },
    {
      title: "Selling Price (RM)",
      key: "purchasePrice",
      align: "center" as const,
      render: (record: any) => (record.sellingPrice === 0 ? "-" : Number(record.sellingPrice).toFixed(2)),
    },
    {
      title: "Revenue (RM)",
      key: "purchaseCost",
      align: "center" as const,
      render: (record: any) => (record.revenue === 0 ? "-" : Number(record.revenue).toFixed(2)),
    },
    {
      title: "Gross Profit (RM)",
      key: "grossProfit",
      align: "center" as const,
      render: (record: any) =>
        record.revenue != 0 ? Number(Number(record.revenue) - Number(record.purchaseCost)).toFixed(2) : "-",
    },
    {
      title: "Gross Profit Percentage",
      key: "grossProfitPercentage",
      align: "center" as const,
      render: (record: any) => {
        let profitPercentageColor = "grey";
        let profitPercentage = Number((Number(record.grossProfit) / Number(record.purchaseCost)) * 100).toFixed(0);
        if (Number(profitPercentage) < 0) profitPercentageColor = "red";
        if (Number(profitPercentage) > 0 && Number(profitPercentage) <= 10) profitPercentageColor = "volcano";
        if (Number(profitPercentage) > 10 && Number(profitPercentage) <= 30) profitPercentageColor = "orange";
        if (Number(profitPercentage) > 30 && Number(profitPercentage) <= 50) profitPercentageColor = "gold";
        if (Number(profitPercentage) > 50 && Number(profitPercentage) <= 80) profitPercentageColor = "lime";
        if (Number(profitPercentage) > 80) profitPercentageColor = "green";
        return Number(profitPercentage) > 0 ? (
          <Tag color={profitPercentageColor}>{`${Number(profitPercentage).toFixed(0)} %`}</Tag>
        ) : (
          "-"
        );
      },
    },
    {
      title: "Days in Inv",
      key: "daysInInv",
      align: "center" as const,
      render: (record: any) => getNumDaysInInv(record),
    },
    {
      title: "Manual Entry",
      key: "manualEntry",
      align: "center" as const,
      render: (record: any) =>
        record.manualEntry ? (
          <>
            <ControlFilled style={{ fontSize: "20px", color: "orange", marginRight: "5px" }} />
            Yes
          </>
        ) : (
          <>
            <ControlFilled style={{ fontSize: "20px", color: "green", marginRight: "5px" }} />
            No
          </>
        ),
    },
    {
      title: "Options",
      key: "options",
      align: "center" as const,
      render: (record: any) => {
        const userRole = cookies.get("USER_ROLE");
        let items: MenuProps["items"] = [];
        items.push({ key: "edit", label: "Add/Edit Buyer Info", icon: <EditOutlined /> });
        return (
          <Dropdown
            menu={{
              items,
              onClick: ({ key }) => {
                // if (key === "edit") openAddEditBuyerInfo(record);
              },
            }}
            trigger={["click"]}
          >
            <a onClick={(e) => e.preventDefault()}>
              <MoreOutlined style={{ fontSize: "24px" }} />
            </a>
          </Dropdown>
        );
      },
    },
  ];

  useEffect(() => {
    navToSelectOffice();
    getMetals();
    getSuppliers();
    getBuyers();
    getCategories();
  }, []);

  useEffect(() => {
    // getOverview(currentPage, suppliersSelectedOptions, dateRangeFilter);
    getOverviewMaterials(currentPage);
  }, [metals]);

  const navToSelectOffice = () => {
    if (currOffice === "" || currOffice === undefined || currOffice === null) navigate("/");
  };

  const toggleSidebar = (collapsed: boolean) => {
    setSidebarCollapsed(collapsed);
  };

  const navToLogin = () => {
    navigate("/login");
  };

  const getMetals = async () => {
    try {
      setLoading(true);
      const res = await getMetalsService();
      setMetals(res.data);
      const options: any = [];
      const currMetalsMap: any = {};
      res.data.forEach((metal: any) => {
        currMetalsMap[metal.metalID] = { ...metal };
        options.push({
          label: metal.displayName,
          value: metal.metalID,
        });
      });
      setMetalsMap(currMetalsMap);
      setLoading(false);
    } catch (e: any) {
      setLoading(false);
      console.error("getMetals error", e);
      if (e.response.status === 401) {
        removeTokenFromCookies();
        navToLogin();
      }
    }
  };

  const getSuppliers = async () => {
    try {
      setLoading(true);
      const res = await getSuppliersService();
      const suppliersData: any = [...res.data.pgSuppliers, ...res.data.bwSuppliers];
      setSuppliers(suppliersData);
      setLoading(false);
    } catch (e: any) {
      setLoading(false);
      console.error("getSuppliers error", e);
      if (e.response.status === 401) {
        removeTokenFromCookies();
        navToLogin();
      }
    }
  };

  const getBuyers = async () => {
    try {
      setLoading(true);
      const res = await getBuyersService();
      const buyersData: any = [...res.data.pgBuyers, ...res.data.bwBuyers];
      setBuyers(buyersData);
      setLoading(false);
    } catch (e: any) {
      setLoading(false);
      console.error("getBuyers error", e);
      if (e.response.status === 401) {
        removeTokenFromCookies();
        navToLogin();
      }
    }
  };

  const getBuyerDisplayName = (buyerID: any) => {
    const buyerInfo = buyers.find((buyer: any) => buyer.buyerID === buyerID);
    // console.log("buyers", buyers);
    // console.log("buyerInfo", buyerInfo);
    let buyerDisplayInfo = "";
    let popoverContent = <></>;
    if (
      buyerInfo !== undefined &&
      buyerInfo["companyName"] !== undefined &&
      buyerInfo["companyName"] !== "" &&
      buyerInfo["name"] !== undefined &&
      buyerInfo["name"] !== ""
    ) {
      buyerDisplayInfo = buyerInfo["companyName"] + " (" + buyerInfo["name"] + ")";
      popoverContent = (
        <div>
          <p>Name: {buyerInfo["name"]}</p>
          <p>Company: {buyerInfo["companyName"]}</p>
          {buyerInfo["identificationCardNumber"] !== "" && <p>IC Number: {buyerInfo["identificationCardNumber"]}</p>}
          {buyerInfo["passportNumber"] !== "" && <p>Passport Number: {buyerInfo["passportNumber"]}</p>}
        </div>
      );
    } else if (buyerInfo !== buyerInfo && buyerInfo["companyName"] !== undefined && buyerInfo["companyName"] !== "") {
      buyerDisplayInfo = buyerInfo["companyName"];
      popoverContent = (
        <div>
          <p>Company: {buyerInfo["companyName"]}</p>
          {buyerInfo["identificationCardNumber"] !== "" && <p>IC Number: {buyerInfo["identificationCardNumber"]}</p>}
          {buyerInfo["passportNumber"] !== "" && <p>Passport Number: {buyerInfo["passportNumber"]}</p>}
        </div>
      );
    } else if (buyerInfo !== undefined && buyerInfo["name"] !== undefined && buyerInfo["name"] !== "") {
      buyerDisplayInfo = buyerInfo["name"];
      popoverContent = (
        <div>
          <p>Name: {buyerInfo["name"]}</p>
          {buyerInfo["identificationCardNumber"] !== "" && <p>IC Number: {buyerInfo["identificationCardNumber"]}</p>}
          {buyerInfo["passportNumber"] !== "" && <p>Passport Number: {buyerInfo["passportNumber"]}</p>}
        </div>
      );
    }
    return <Popover content={popoverContent}>{buyerDisplayInfo}</Popover>;
  };

  const getCategories = async () => {
    try {
      setLoading(true);
      const res = await getCategoriesService();
      setMetals(res.data);
      const options: any = [];
      const categories: any = [];
      res.data.forEach((category: any) => {
        // console.log("categories", categories);
        categories.push(category);
      });
      setCategories(categories);
      setLoading(false);
    } catch (e: any) {
      setLoading(false);
      console.error("getMetals error", e);
      if (e.response.status === 401) {
        removeTokenFromCookies();
        navToLogin();
      }
    }
  };

  const getNumDaysInInv = (record: any) => {
    let currentDate: any = new Date();
    let purchaseDate: any = new Date(record.purchaseCreatedTimestamp);
    let daysDifference: any = currentDate - purchaseDate;
    let daysInInv = Math.ceil(daysDifference / (1000 * 3600 * 24));
    if (record.soldTimestamp !== null && record.soldTimestamp !== undefined) {
      let soldDate: any = new Date(record.soldTimestamp);
      daysDifference = soldDate - purchaseDate;
      daysInInv = Math.ceil(daysDifference / (1000 * 3600 * 24));
    }
    return daysInInv;
  };

  const getOverviewMaterials = async (pageNumber: Number) => {
    try {
      setLoading(true);
      const filters = {};
      //   suppliers: suppliersFilter,
      //   startDate: dateRangeFilter[0] === null || dateRangeFilter.length === 0 ? "" : dateRangeFilter[0],
      //   endDate: dateRangeFilter[1] === null || dateRangeFilter.length === 0 ? "" : dateRangeFilter[1],
      // };
      const resOverviewMaterials = await getOverviewMaterialsService(String(pageNumber), filters);
      setTotalPages(resOverviewMaterials.data.totalPages);
      setTotalOverviewMaterials(resOverviewMaterials.data.totalOverviewMaterials);
      const currOverviewMaterials: any[] = [];
      for (let overviewMaterial of resOverviewMaterials.data.overviewmaterials) {
        overviewMaterial.key = overviewMaterial.overviewMaterialID;
        // const purchaseID = bill.purchaseID;
        // const resPurchase = await getPurchase(purchaseID);
        // bill.materialIDList = resPurchase?.data.materialIDList;
        currOverviewMaterials.push(overviewMaterial);
      }
      setOverviewMaterialsList([...currOverviewMaterials]);
      setLoading(false);
    } catch (e: any) {
      setLoading(false);
      console.error("getOverviewMaterials error", e);
      if (e.response.status === 401) {
        removeTokenFromCookies();
        navToLogin();
      }
    }
  };

  const getSupplierDisplayName = (supplierID: any) => {
    const supplierInfo = suppliers.find((supplier: any) => supplier.supplierID === supplierID);
    let supplierDisplayInfo = "";
    let popoverContent = <></>;
    if (
      supplierInfo !== undefined &&
      supplierInfo["companyName"] !== undefined &&
      supplierInfo["companyName"] !== "" &&
      supplierInfo["name"] !== undefined &&
      supplierInfo["name"] !== ""
    ) {
      supplierDisplayInfo = supplierInfo["companyName"] + " (" + supplierInfo["name"] + ")";
      popoverContent = (
        <div>
          <p>Name: {supplierInfo["name"]}</p>
          <p>Company: {supplierInfo["companyName"]}</p>
          {supplierInfo["identificationCardNumber"] !== "" && (
            <p>IC Number: {supplierInfo["identificationCardNumber"]}</p>
          )}
          {supplierInfo["passportNumber"] !== "" && <p>Passport Number: {supplierInfo["passportNumber"]}</p>}
        </div>
      );
    } else if (
      supplierInfo !== supplierInfo &&
      supplierInfo["companyName"] !== undefined &&
      supplierInfo["companyName"] !== ""
    ) {
      supplierDisplayInfo = supplierInfo["companyName"];
      popoverContent = (
        <div>
          <p>Company: {supplierInfo["companyName"]}</p>
          {supplierInfo["identificationCardNumber"] !== "" && (
            <p>IC Number: {supplierInfo["identificationCardNumber"]}</p>
          )}
          {supplierInfo["passportNumber"] !== "" && <p>Passport Number: {supplierInfo["passportNumber"]}</p>}
        </div>
      );
    } else if (supplierInfo !== undefined && supplierInfo["name"] !== undefined && supplierInfo["name"] !== "") {
      supplierDisplayInfo = supplierInfo["name"];
      popoverContent = (
        <div>
          <p>Name: {supplierInfo["name"]}</p>
          {supplierInfo["identificationCardNumber"] !== "" && (
            <p>IC Number: {supplierInfo["identificationCardNumber"]}</p>
          )}
          {supplierInfo["passportNumber"] !== "" && <p>Passport Number: {supplierInfo["passportNumber"]}</p>}
        </div>
      );
    }
    return <Popover content={popoverContent}>{supplierDisplayInfo}</Popover>;
  };

  return (
    <Layout>
      <Loader loading={loading} />
      <Sidebar collapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} />
      <Content>
        <Header toggleSidebar={toggleSidebar} />
        <Row justify="center">
          <Col span={22}>
            <Row>
              <Col span={12}>
                <Row justify="start">
                  <h2 className="average-prices-title">Overview Materials</h2>
                </Row>
              </Col>
              <Col span={12}>
                <Row justify="end" style={{ marginTop: "20px" }}>
                  {/* <Button type="primary" onClick={() => openNewAverageForm()}>
                    Get New Average
                  </Button> */}
                </Row>
              </Col>
            </Row>
            <Divider style={{ margin: "12px 0" }} />
            <Table
              dataSource={[...overviewMaterialsList]}
              columns={materialsColumns}
              rowKey={(record: any) => record.key}
              pagination={{
                pageSize: 20,
                total: totalOverviewMaterials,
                showSizeChanger: false,
                onChange: (page) => {
                  setCurrentPage(page);
                  getOverviewMaterials(page);
                },
              }}
            />
          </Col>
        </Row>
        <Copyright />
      </Content>
    </Layout>
  );
};

export default OverviewMaterialsPage;
